import {
	type ColumnDef,
	flexRender,
	getCoreRowModel,
	getSortedRowModel,
	type SortingState,
	useReactTable,
} from "@tanstack/react-table";
import { useAction, useMutation, useQuery } from "convex/react";
import {
	ActivityIcon,
	ArrowUpDownIcon,
	ChevronRightIcon,
	CloudCogIcon,
	CopyIcon,
	ExternalLinkIcon,
	KeyRoundIcon,
	PauseIcon,
	PlayIcon,
	PlusIcon,
	RefreshCwIcon,
	Trash2Icon,
} from "lucide-react";
import { useCallback, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import {
	Empty,
	EmptyContent,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from "@/components/ui/empty";
import {
	Field,
	FieldDescription,
	FieldGroup,
	FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { Switch } from "@/components/ui/switch";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { api } from "../../../../convex/_generated/api";
import type { Id } from "../../../../convex/_generated/dataModel";

import {
	ANTHROPIC_OAUTH_POPUP_MESSAGE,
	type AnthropicOAuthPopupResult,
	anthropicOAuthChannelName,
	parseAnthropicOAuthCallback,
} from "./anthropicOAuthPopup";
import { displayQuotaWindows, type QuotaWindowSource } from "./quotaWindows";

const dateTime = new Intl.DateTimeFormat(undefined, {
	dateStyle: "medium",
	timeStyle: "short",
});
const shortDate = new Intl.DateTimeFormat(undefined, { dateStyle: "medium" });
const resetTime = new Intl.DateTimeFormat(undefined, {
	month: "short",
	day: "numeric",
	hour: "2-digit",
	minute: "2-digit",
});
const number = new Intl.NumberFormat();
const currency = new Intl.NumberFormat(undefined, {
	style: "currency",
	currency: "USD",
	minimumFractionDigits: 4,
	maximumFractionDigits: 6,
});
const perMillion = new Intl.NumberFormat(undefined, {
	style: "currency",
	currency: "USD",
	minimumFractionDigits: 2,
	maximumFractionDigits: 2,
});

type AccountStatus =
	| "active"
	| "paused"
	| "reconnect_required"
	| "revoking"
	| "revoked";
export type AccountRow = {
	accountId: Id<"llmSubscriptionAccounts">;
	provider: "codex" | "anthropic";
	label: string;
	providerEmail: string;
	providerAccountIdMasked: string;
	providerPlan?: string;
	providerOrganizationIdMasked?: string;
	providerOrganizationName?: string;
	authorizedAt?: number;
	grantExpiresAt?: number;
	status: AccountStatus;
	modelCount: number;
	primaryUsagePercent?: number;
	primaryResetAt?: number;
	secondaryUsagePercent?: number;
	secondaryResetAt?: number;
	cacheMode?: "explicit" | "implicit";
	lastCachedInputTokens?: number;
	lastRequestAt?: number;
	lastSuccessAt?: number;
	lastHealthAt?: number;
	lastErrorCode?: string;
	updatedAt: number;
	quotaWindows: Array<{
		scopeKey: string;
		usagePercent?: number;
		resetAt?: number;
		blockedUntil?: number;
		fetchedAt: number;
	}>;
};
type DeviceAttempt = {
	attemptId: Id<"llmSubscriptionAuthAttempts">;
	verificationUrl: string;
	userCode: string;
	intervalSeconds: number;
	expiresAt: number;
	nextPollAt: number;
};
type ManualAnthropicOAuth = {
	expectedRedirectUri: string;
	state: string;
};

function formatDate(value: number | undefined): string {
	return value === undefined ? "Never" : dateTime.format(value);
}

function formatRate(perToken: number): string {
	return perMillion.format(perToken * 1_000_000);
}

function statusLabel(status: AccountStatus): string {
	if (status === "reconnect_required") return "Reconnect required";
	if (status === "revoking") return "Revoking";
	return `${status.charAt(0).toUpperCase()}${status.slice(1)}`;
}

function StatusBadge({ status }: { status: AccountStatus }) {
	return (
		<Badge
			variant={
				status === "active"
					? "secondary"
					: status === "revoked"
						? "destructive"
						: "outline"
			}
		>
			{statusLabel(status)}
		</Badge>
	);
}

function errorCopy(error: unknown): string {
	const message = error instanceof Error ? error.message : String(error);
	if (message.includes("subscription.oauth.identity_mismatch")) {
		return "Connected identity differs from bound account. Connect it as a new account.";
	}
	if (message.includes("subscription.oauth.duplicate_identity")) {
		return "Provider identity is already connected. Reconnect its existing account.";
	}
	if (message.includes("subscription.cache.unverified")) {
		return "Provider returned no cached-token proof. Direct routing remains disabled.";
	}
	if (message.includes("subscription.account_revoking")) {
		return "Account revocation is pending. Retry revoke to finish removing credentials.";
	}
	if (message.includes("subscription.oauth.callback_redirect_mismatch")) {
		return "Paste the full localhost callback URL from the Anthropic popup.";
	}
	if (message.includes("subscription.oauth.state_mismatch")) {
		return "Callback belongs to a different login attempt. Start authorization again.";
	}
	if (message.includes("subscription.oauth.provider_denied")) {
		return "Anthropic authorization was denied. Start authorization again.";
	}
	if (message.includes("subscription.oauth.callback_invalid")) {
		return "Paste the complete localhost callback URL, including code and state.";
	}
	if (
		message.includes("subscription.oauth.reconnect_required") ||
		message.includes("subscription.account_reconnect_required")
	) {
		return "Provider session expired. Reconnect account and try again.";
	}
	if (
		message.includes("subscription.oauth.expired") ||
		message.includes("subscription.oauth.attempt_")
	) {
		return "Device login expired or is no longer available. Start it again.";
	}
	if (message.includes("subscription.oauth.incomplete_bundle")) {
		return "Provider returned an incomplete credential bundle. Start device login again.";
	}
	if (
		message.includes("subscription.oauth.invalid_id_token") ||
		message.includes("subscription.oauth.email_unverified") ||
		message.includes("subscription.oauth.account_missing") ||
		message.includes("subscription.oauth.discovery_failed") ||
		message.includes("subscription.oauth.userinfo_failed")
	) {
		return "Codex identity could not be verified. Sign in again, then retry.";
	}
	if (message.includes("subscription.oauth.revoke_transient")) {
		return "Provider revocation failed. Account remains paused with encrypted credentials intact.";
	}
	if (message.includes("subscription.provider.rate_limit")) {
		return "Provider quota is exhausted. Wait for reset, then test account.";
	}
	if (message.includes("subscription.catalog")) {
		return "Live Codex model catalog could not be verified. Retry account test.";
	}
	if (message.includes("subscription.pricing")) {
		return "OpenRouter reference pricing could not be verified. Retry account test.";
	}
	if (message.includes("subscription.account_not_found")) {
		return "Subscription account no longer exists. Refresh account list.";
	}
	if (message.includes("subscription.account_revoked")) {
		return "Revoked account is a permanent tombstone. Connect a new account.";
	}
	if (message.includes("subscription.account.concurrent_update")) {
		return "Account changed during this action. Refresh account list and retry.";
	}
	if (message.includes("subscription.account_limit")) {
		return "Subscription account limit reached. Revoke an unused account before adding another.";
	}
	if (message.includes("subscription.account_not_active")) {
		return "Resume subscription account before testing it.";
	}
	if (message.includes("subscription.account.invalid_label")) {
		return "Label must contain 1–80 characters.";
	}
	if (message.includes("subscription.encryption.misconfigured")) {
		return "Credential encryption is not configured for this deployment.";
	}
	if (message.includes("subscription.protocol")) {
		return "Codex protocol probe failed. Engineering review required.";
	}
	return "Subscription account action failed. Retry or inspect account health.";
}

async function copyText(value: string, successMessage: string) {
	try {
		await navigator.clipboard.writeText(value);
		toast.success(successMessage);
	} catch {
		toast.error("Could not copy. Select and copy it manually.");
	}
}

function QuotaWindow({
	label,
	usagePercent,
	resetAt,
}: {
	label: string;
	usagePercent?: number;
	resetAt?: number;
}) {
	return (
		<div className="flex items-center gap-2">
			<span className="w-32 shrink-0 text-xs text-muted-foreground">
				{label}
			</span>
			{usagePercent !== undefined ? (
				<>
					<Progress
						value={Math.min(100, usagePercent)}
						className="h-1.5 w-20"
					/>
					<span className="text-xs tabular-nums">
						{Math.round(usagePercent)}%
					</span>
				</>
			) : null}
			{resetAt !== undefined ? (
				<span className="truncate text-xs text-muted-foreground">
					resets {resetTime.format(resetAt)}
				</span>
			) : null}
		</div>
	);
}

function QuotaCell({ account }: { account: AccountRow }) {
	const windows = displayQuotaWindows(account);
	if (windows.length === 0) {
		return <span className="text-xs text-muted-foreground">Unknown</span>;
	}
	return (
		<div className="flex flex-col gap-1">
			{windows.map((window) => (
				<QuotaWindow
					key={window.key}
					label={window.label}
					usagePercent={window.usagePercent}
					resetAt={window.resetAt}
				/>
			))}
		</div>
	);
}

function QuotaWindowsCard({ account }: { account: QuotaWindowSource }) {
	const windows = displayQuotaWindows(account);
	if (windows.length === 0) return null;
	return (
		<Card>
			<CardHeader>
				<CardTitle>Quota windows</CardTitle>
				<CardDescription>
					Provider-reported usage against subscription limits.
				</CardDescription>
			</CardHeader>
			<CardContent className="flex flex-col gap-2">
				{windows.map((window) => (
					<QuotaWindow
						key={window.key}
						label={window.label}
						usagePercent={window.usagePercent}
						resetAt={window.resetAt}
					/>
				))}
			</CardContent>
		</Card>
	);
}

function SortableHead({
	label,
	onToggle,
}: {
	label: string;
	onToggle: () => void;
}) {
	return (
		<button
			type="button"
			className="flex items-center gap-1 hover:text-foreground"
			onClick={onToggle}
		>
			{label}
			<ArrowUpDownIcon className="size-3" />
		</button>
	);
}

const accountColumns: ColumnDef<AccountRow>[] = [
	{
		id: "label",
		accessorKey: "label",
		header: ({ column }) => (
			<SortableHead
				label="Account"
				onToggle={() => column.toggleSorting(column.getIsSorted() === "asc")}
			/>
		),
		cell: ({ row }) => (
			<div className="flex flex-col gap-0.5">
				<div className="flex items-center gap-2">
					<span className="font-medium">{row.original.label}</span>
					<Badge variant="outline">
						{row.original.provider === "anthropic" ? "Anthropic" : "Codex"}
					</Badge>
				</div>
				<span className="text-xs text-muted-foreground">
					{row.original.providerEmail}
					{row.original.providerPlan ? ` · ${row.original.providerPlan}` : ""}
				</span>
			</div>
		),
	},
	{
		id: "status",
		accessorKey: "status",
		header: ({ column }) => (
			<SortableHead
				label="Status"
				onToggle={() => column.toggleSorting(column.getIsSorted() === "asc")}
			/>
		),
		cell: ({ row }) => <StatusBadge status={row.original.status} />,
	},
	{
		id: "models",
		accessorKey: "modelCount",
		header: "Models",
		cell: ({ row }) => (
			<span className="tabular-nums">{row.original.modelCount}</span>
		),
	},
	{
		id: "quota",
		header: "Quota",
		cell: ({ row }) => <QuotaCell account={row.original} />,
	},
	{
		id: "cache",
		header: "Cache",
		cell: ({ row }) => (
			<div className="flex flex-col gap-0.5">
				<span>{row.original.cacheMode ?? "Unverified"}</span>
				<span className="text-xs tabular-nums text-muted-foreground">
					{number.format(row.original.lastCachedInputTokens ?? 0)} tokens
				</span>
			</div>
		),
	},
	{
		id: "lastRequestAt",
		accessorKey: "lastRequestAt",
		header: ({ column }) => (
			<SortableHead
				label="Last request"
				onToggle={() => column.toggleSorting(column.getIsSorted() === "asc")}
			/>
		),
		cell: ({ row }) => (
			<span className="text-muted-foreground">
				{formatDate(row.original.lastRequestAt)}
			</span>
		),
	},
	{
		id: "health",
		header: "Health",
		cell: ({ row }) => (
			<div className="flex flex-col gap-0.5">
				<span
					className={cn(
						row.original.lastErrorCode ? "text-destructive" : undefined,
					)}
				>
					{row.original.lastErrorCode ?? "Healthy"}
				</span>
				<span className="text-xs text-muted-foreground">
					{formatDate(row.original.lastHealthAt)}
				</span>
			</div>
		),
	},
	{
		id: "open",
		header: "",
		cell: () => <ChevronRightIcon className="size-4 text-muted-foreground" />,
	},
];

function AccountsTable({
	accounts,
	onSelect,
}: {
	accounts: AccountRow[];
	onSelect: (accountId: Id<"llmSubscriptionAccounts">) => void;
}) {
	const [sorting, setSorting] = useState<SortingState>([]);
	const table = useReactTable({
		data: accounts,
		columns: accountColumns,
		state: { sorting },
		onSortingChange: setSorting,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
	});
	return (
		<Table>
			<TableHeader>
				{table.getHeaderGroups().map((headerGroup) => (
					<TableRow key={headerGroup.id}>
						{headerGroup.headers.map((header) => (
							<TableHead key={header.id}>
								{header.isPlaceholder
									? null
									: flexRender(
											header.column.columnDef.header,
											header.getContext(),
										)}
							</TableHead>
						))}
					</TableRow>
				))}
			</TableHeader>
			<TableBody>
				{table.getRowModel().rows.map((row) => (
					<TableRow
						key={row.id}
						className="cursor-pointer"
						onClick={() => onSelect(row.original.accountId)}
					>
						{row.getVisibleCells().map((cell) => (
							<TableCell key={cell.id}>
								{flexRender(cell.column.columnDef.cell, cell.getContext())}
							</TableCell>
						))}
					</TableRow>
				))}
			</TableBody>
		</Table>
	);
}

export function ConnectAccountDialog({
	open,
	onOpenChange,
	reconnectAccount,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	reconnectAccount: AccountRow | null;
}) {
	const startDeviceLogin = useAction(
		api.llmSubscriptions.codexActions.startDeviceLogin,
	);
	const pollDeviceLogin = useAction(
		api.llmSubscriptions.codexActions.pollDeviceLogin,
	);
	const startAnthropicOAuth = useAction(
		api.llmSubscriptions.anthropicActions.startOAuth,
	);
	const pollAnthropicOAuth = useAction(
		api.llmSubscriptions.anthropicActions.pollOAuth,
	);
	const completeAnthropicOAuth = useAction(
		api.llmSubscriptions.anthropicActions.completeOAuth,
	);
	const [provider, setProvider] = useState<"codex" | "anthropic">(
		() => reconnectAccount?.provider ?? "codex",
	);
	const [label, setLabel] = useState(
		() =>
			reconnectAccount?.label ??
			(provider === "anthropic"
				? "Anthropic subscription"
				: "Codex subscription"),
	);
	const [deviceAttempt, setDeviceAttempt] = useState<DeviceAttempt | null>(
		null,
	);
	const [oauthWaiting, setOAuthWaiting] = useState(false);
	const [manualAnthropicOAuth, setManualAnthropicOAuth] =
		useState<ManualAnthropicOAuth | null>(null);
	const [anthropicCallbackUrl, setAnthropicCallbackUrl] = useState("");
	const [pending, setPending] = useState(false);
	const pollTokenRef = useRef(0);
	const popupRef = useRef<Window | null>(null);
	const oauthChannelRef = useRef<BroadcastChannel | null>(null);

	const handleOAuthResult = useCallback(
		(data: AnthropicOAuthPopupResult) => {
			pollTokenRef.current += 1;
			popupRef.current?.close();
			popupRef.current = null;
			oauthChannelRef.current?.close();
			oauthChannelRef.current = null;
			setOAuthWaiting(false);
			setManualAnthropicOAuth(null);
			setAnthropicCallbackUrl("");
			if (data.status === "connected") {
				toast.success("Anthropic account connected and cache verified");
				onOpenChange(false);
				return;
			}
			toast.error(errorCopy(new Error(data.code)));
		},
		[onOpenChange],
	);

	const connectLifecycleRef = useCallback(
		(node: HTMLDivElement | null) => {
			if (!node) return;
			const handleMessage = (event: MessageEvent<unknown>) => {
				if (
					event.origin !== window.location.origin ||
					event.source !== popupRef.current ||
					!event.data ||
					typeof event.data !== "object" ||
					Array.isArray(event.data)
				) {
					return;
				}
				const data = Object.fromEntries(Object.entries(event.data));
				if (
					data.type !== ANTHROPIC_OAUTH_POPUP_MESSAGE ||
					(data.status !== "connected" && data.status !== "error")
				) {
					return;
				}
				handleOAuthResult(
					data.status === "connected"
						? { type: ANTHROPIC_OAUTH_POPUP_MESSAGE, status: "connected" }
						: {
								type: ANTHROPIC_OAUTH_POPUP_MESSAGE,
								status: "error",
								code:
									typeof data.code === "string"
										? data.code
										: "subscription.oauth.callback_failed",
							},
				);
			};
			window.addEventListener("message", handleMessage);
			return () => {
				pollTokenRef.current += 1;
				popupRef.current?.close();
				oauthChannelRef.current?.close();
				oauthChannelRef.current = null;
				window.removeEventListener("message", handleMessage);
			};
		},
		[handleOAuthResult],
	);

	function handleOpenChange(next: boolean) {
		if (!next) {
			pollTokenRef.current += 1;
			popupRef.current?.close();
			popupRef.current = null;
			oauthChannelRef.current?.close();
			oauthChannelRef.current = null;
			setOAuthWaiting(false);
			setManualAnthropicOAuth(null);
			setAnthropicCallbackUrl("");
		}
		onOpenChange(next);
	}

	function changeProvider(next: "codex" | "anthropic") {
		if (label === "Codex subscription" || label === "Anthropic subscription") {
			setLabel(
				next === "anthropic" ? "Anthropic subscription" : "Codex subscription",
			);
		}
		setProvider(next);
	}

	async function runPollLoop(attempt: DeviceAttempt) {
		const token = ++pollTokenRef.current;
		let nextPollAt = attempt.nextPollAt;
		while (true) {
			const delay = Math.max(0, nextPollAt - Date.now());
			await new Promise<void>((resolve) => window.setTimeout(resolve, delay));
			if (pollTokenRef.current !== token) return;
			try {
				const result = await pollDeviceLogin({ attemptId: attempt.attemptId });
				if (pollTokenRef.current !== token) return;
				if (result.status === "connected") {
					toast.success("Codex account connected and cache verified");
					onOpenChange(false);
					return;
				}
				nextPollAt = result.nextPollAt;
			} catch (error) {
				if (pollTokenRef.current !== token) return;
				toast.error(errorCopy(error));
				setDeviceAttempt(null);
				return;
			}
		}
	}

	async function runAnthropicPollLoop(
		attemptId: Id<"llmSubscriptionAnthropicOAuthAttempts">,
	) {
		const token = ++pollTokenRef.current;
		while (pollTokenRef.current === token) {
			const result = await pollAnthropicOAuth({ attemptId });
			if (pollTokenRef.current !== token) return;
			if (result.status === "connected") {
				handleOAuthResult({
					type: ANTHROPIC_OAUTH_POPUP_MESSAGE,
					status: "connected",
				});
				return;
			}
			if (result.status === "failed" || result.status === "expired") {
				handleOAuthResult({
					type: ANTHROPIC_OAUTH_POPUP_MESSAGE,
					status: "error",
					code: result.code,
				});
				return;
			}
			await new Promise<void>((resolve) => window.setTimeout(resolve, 1_000));
		}
	}

	async function completeManualAnthropicOAuth() {
		if (!manualAnthropicOAuth) return;
		let callback: { code: string; state: string };
		try {
			callback = parseAnthropicOAuthCallback(
				anthropicCallbackUrl,
				manualAnthropicOAuth.expectedRedirectUri,
				manualAnthropicOAuth.state,
			);
		} catch (error) {
			toast.error(errorCopy(error));
			return;
		}
		setPending(true);
		try {
			await completeAnthropicOAuth(callback);
			handleOAuthResult({
				type: ANTHROPIC_OAUTH_POPUP_MESSAGE,
				status: "connected",
			});
		} catch (error) {
			pollTokenRef.current += 1;
			popupRef.current?.close();
			popupRef.current = null;
			setOAuthWaiting(false);
			setManualAnthropicOAuth(null);
			setAnthropicCallbackUrl("");
			toast.error(errorCopy(error));
		} finally {
			setPending(false);
		}
	}

	async function beginConnection() {
		if (provider === "anthropic") {
			const popup = window.open(
				"about:blank",
				"clerical-anthropic-oauth",
				"popup,width=520,height=720",
			);
			if (!popup) {
				toast.error("Allow popups, then retry Anthropic authorization.");
				return;
			}
			popupRef.current = popup;
			setPending(true);
			setOAuthWaiting(true);
			try {
				const result = await startAnthropicOAuth({
					label,
					accountId: reconnectAccount?.accountId,
				});
				const authorizationUrl = new URL(result.authorizationUrl);
				const state = authorizationUrl.searchParams.get("state");
				const redirectUri = authorizationUrl.searchParams.get("redirect_uri");
				if (!state || !redirectUri) {
					throw new Error("subscription.oauth.callback_invalid");
				}
				const automaticCallback =
					new URL(redirectUri).origin === window.location.origin;
				if (!automaticCallback) {
					setManualAnthropicOAuth({
						expectedRedirectUri: redirectUri,
						state,
					});
					setAnthropicCallbackUrl("");
				}
				if (automaticCallback && typeof BroadcastChannel !== "undefined") {
					const channel = new BroadcastChannel(
						anthropicOAuthChannelName(state),
					);
					channel.onmessage = (event: MessageEvent<unknown>) => {
						if (
							!event.data ||
							typeof event.data !== "object" ||
							Array.isArray(event.data)
						) {
							return;
						}
						const data = Object.fromEntries(Object.entries(event.data));
						if (
							data.type !== ANTHROPIC_OAUTH_POPUP_MESSAGE ||
							(data.status !== "connected" && data.status !== "error")
						) {
							return;
						}
						handleOAuthResult(
							data.status === "connected"
								? {
										type: ANTHROPIC_OAUTH_POPUP_MESSAGE,
										status: "connected",
									}
								: {
										type: ANTHROPIC_OAUTH_POPUP_MESSAGE,
										status: "error",
										code:
											typeof data.code === "string"
												? data.code
												: "subscription.oauth.callback_failed",
									},
						);
					};
					oauthChannelRef.current = channel;
				}
				void runAnthropicPollLoop(result.attemptId);
				popup.location.assign(result.authorizationUrl);
			} catch (error) {
				popup.close();
				popupRef.current = null;
				setOAuthWaiting(false);
				toast.error(errorCopy(error));
			} finally {
				setPending(false);
			}
			return;
		}
		setPending(true);
		try {
			const result = await startDeviceLogin({
				label,
				accountId: reconnectAccount?.accountId,
			});
			const attempt: DeviceAttempt = { ...result, nextPollAt: Date.now() };
			setDeviceAttempt(attempt);
			void runPollLoop(attempt);
		} catch (error) {
			toast.error(errorCopy(error));
		} finally {
			setPending(false);
		}
	}

	const busy = pending || deviceAttempt !== null || oauthWaiting;
	const providerName = provider === "anthropic" ? "Anthropic" : "Codex";

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogContent className="sm:max-w-xl">
				<ScrollArea className="max-h-[calc(100vh-4rem)]">
					<div ref={connectLifecycleRef} className="flex flex-col gap-4">
						<DialogHeader>
							<DialogTitle>
								{reconnectAccount
									? `Reconnect ${providerName} account`
									: "Connect LLM subscription"}
							</DialogTitle>
							<DialogDescription>
								Credentials stay encrypted. Activation verifies identity, direct
								model access, quota, pricing, and prompt-cache continuity.
							</DialogDescription>
						</DialogHeader>
						<FieldGroup>
							{!reconnectAccount ? (
								<Field>
									<FieldLabel>Provider</FieldLabel>
									<div className="grid grid-cols-2 gap-2">
										<Button
											type="button"
											variant={provider === "codex" ? "default" : "outline"}
											disabled={busy}
											onClick={() => changeProvider("codex")}
										>
											Codex
										</Button>
										<Button
											type="button"
											variant={provider === "anthropic" ? "default" : "outline"}
											disabled={busy}
											onClick={() => changeProvider("anthropic")}
										>
											Anthropic
										</Button>
									</div>
								</Field>
							) : null}
							<Field>
								<FieldLabel htmlFor="subscription-label">Label</FieldLabel>
								<Input
									id="subscription-label"
									value={label}
									maxLength={80}
									disabled={busy}
									onChange={(event) => setLabel(event.target.value)}
								/>
							</Field>
							{reconnectAccount ? (
								<Field>
									<FieldLabel htmlFor="subscription-bound-identity">
										Bound identity
									</FieldLabel>
									<Input
										id="subscription-bound-identity"
										value={reconnectAccount.providerEmail}
										disabled
									/>
									<FieldDescription>
										Replacement must verify this provider account
										{reconnectAccount.providerOrganizationName
											? ` in ${reconnectAccount.providerOrganizationName}`
											: ""}
										.
									</FieldDescription>
								</Field>
							) : null}
						</FieldGroup>
						<Card>
							<CardHeader>
								<CardTitle>
									{provider === "anthropic"
										? "PKCE authorization"
										: "Browser authorization"}
								</CardTitle>
								<CardDescription>
									{provider === "anthropic"
										? "Sign in with Claude Pro or Max in popup, then return here."
										: "Open OpenAI verification, enter one-time code, then leave dialog open."}
								</CardDescription>
							</CardHeader>
							<CardContent className="flex flex-col gap-4">
								{provider === "codex" && deviceAttempt ? (
									<>
										<div className="flex items-center justify-between gap-3 rounded-lg border p-3">
											<code className="text-lg font-semibold tracking-[0.18em]">
												{deviceAttempt.userCode}
											</code>
											<Button
												variant="outline"
												size="sm"
												onClick={() =>
													copyText(deviceAttempt.userCode, "Code copied")
												}
											>
												<CopyIcon data-icon="inline-start" />
												Copy
											</Button>
										</div>
										<Button
											nativeButton={false}
											render={
												<a
													href={deviceAttempt.verificationUrl}
													target="_blank"
													rel="noreferrer"
												/>
											}
										>
											<ExternalLinkIcon data-icon="inline-start" />
											Open verification
										</Button>
										<div
											role="status"
											aria-live="polite"
											className="flex items-center gap-2 text-sm text-muted-foreground"
										>
											<Spinner />
											Waiting for provider authorization…
										</div>
									</>
								) : oauthWaiting && manualAnthropicOAuth ? (
									<>
										<div className="rounded-lg border bg-muted/40 p-3 text-sm">
											After authorization, popup opens a localhost address and
											may show a connection error. Copy its full address from
											browser bar, then paste it below.
										</div>
										<Field>
											<FieldLabel htmlFor="anthropic-oauth-callback">
												Callback URL
											</FieldLabel>
											<Input
												id="anthropic-oauth-callback"
												value={anthropicCallbackUrl}
												placeholder={`${manualAnthropicOAuth.expectedRedirectUri}?code=…&state=…`}
												autoComplete="off"
												spellCheck={false}
												disabled={pending}
												onChange={(event) =>
													setAnthropicCallbackUrl(event.target.value)
												}
											/>
											<FieldDescription>
												Expected callback:{" "}
												<code>{manualAnthropicOAuth.expectedRedirectUri}</code>
											</FieldDescription>
										</Field>
										<Button
											disabled={
												pending || anthropicCallbackUrl.trim().length === 0
											}
											onClick={completeManualAnthropicOAuth}
										>
											{pending ? (
												<Spinner data-icon="inline-start" />
											) : (
												<KeyRoundIcon data-icon="inline-start" />
											)}
											Complete connection
										</Button>
									</>
								) : oauthWaiting ? (
									<div
										role="status"
										aria-live="polite"
										className="flex items-center gap-2 text-sm text-muted-foreground"
									>
										<Spinner />
										Waiting for Anthropic authorization…
									</div>
								) : (
									<Button
										disabled={pending || label.trim().length === 0}
										onClick={beginConnection}
									>
										{pending ? (
											<Spinner data-icon="inline-start" />
										) : (
											<KeyRoundIcon data-icon="inline-start" />
										)}
										{provider === "anthropic"
											? "Continue with Anthropic"
											: "Start device login"}
									</Button>
								)}
							</CardContent>
						</Card>
						<DialogFooter>
							<Button variant="outline" onClick={() => handleOpenChange(false)}>
								Cancel
							</Button>
						</DialogFooter>
					</div>
				</ScrollArea>
			</DialogContent>
		</Dialog>
	);
}

type TariffRow = {
	subscriptionModelId: string;
	equivalentOpenRouterModelId: string;
	baseRates: {
		prompt: number;
		completion: number;
		request: number;
		internalReasoning: number;
		inputCacheRead: number;
		inputCacheWrite: number;
	};
	overrides: unknown[];
	fetchedAt: number;
	expiresAt: number;
	pricingVersion: string;
};

function TariffsCard({ tariffs }: { tariffs: TariffRow[] }) {
	const showReasoning = tariffs.some(
		(tariff) => tariff.baseRates.internalReasoning > 0,
	);
	const showRequest = tariffs.some((tariff) => tariff.baseRates.request > 0);
	return (
		<Card>
			<CardHeader>
				<CardTitle>Tariffs</CardTitle>
				<CardDescription>
					Immutable OpenRouter-equivalent reference rates, USD per 1M tokens.
				</CardDescription>
			</CardHeader>
			<CardContent className="p-0">
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead>Model</TableHead>
							<TableHead className="text-right">Input</TableHead>
							<TableHead className="text-right">Output</TableHead>
							<TableHead className="text-right">Cache read</TableHead>
							<TableHead className="text-right">Cache write</TableHead>
							{showReasoning ? (
								<TableHead className="text-right">Reasoning</TableHead>
							) : null}
							{showRequest ? (
								<TableHead className="text-right">Request</TableHead>
							) : null}
						</TableRow>
					</TableHeader>
					<TableBody>
						{tariffs.map((tariff) => (
							<TableRow key={tariff.pricingVersion}>
								<TableCell>
									<div className="flex flex-col gap-1">
										<div className="flex items-center gap-2">
											<span className="font-medium">
												{tariff.subscriptionModelId}
											</span>
											{tariff.overrides.length > 0 ? (
												<Badge variant="outline">
													{tariff.overrides.length} overrides
												</Badge>
											) : null}
										</div>
										<div className="flex items-center gap-1">
											<code className="max-w-40 truncate text-xs text-muted-foreground">
												{tariff.pricingVersion}
											</code>
											<Button
												variant="ghost"
												size="icon-sm"
												className="size-5"
												aria-label="Copy pricing version"
												onClick={() =>
													copyText(
														tariff.pricingVersion,
														"Pricing version copied",
													)
												}
											>
												<CopyIcon className="size-3" />
											</Button>
										</div>
										<span className="text-xs text-muted-foreground">
											expires {shortDate.format(tariff.expiresAt)}
										</span>
									</div>
								</TableCell>
								<TableCell className="text-right tabular-nums">
									{formatRate(tariff.baseRates.prompt)}
								</TableCell>
								<TableCell className="text-right tabular-nums">
									{formatRate(tariff.baseRates.completion)}
								</TableCell>
								<TableCell className="text-right tabular-nums">
									{formatRate(tariff.baseRates.inputCacheRead)}
								</TableCell>
								<TableCell className="text-right tabular-nums">
									{formatRate(tariff.baseRates.inputCacheWrite)}
								</TableCell>
								{showReasoning ? (
									<TableCell className="text-right tabular-nums">
										{formatRate(tariff.baseRates.internalReasoning)}
									</TableCell>
								) : null}
								{showRequest ? (
									<TableCell className="text-right tabular-nums">
										{perMillion.format(tariff.baseRates.request)}
									</TableCell>
								) : null}
							</TableRow>
						))}
					</TableBody>
				</Table>
			</CardContent>
		</Card>
	);
}

function DeleteAccountDialog({
	open,
	pending,
	onOpenChange,
	onConfirm,
}: {
	open: boolean;
	pending: boolean;
	onOpenChange: (open: boolean) => void;
	onConfirm: () => void;
}) {
	return (
		<AlertDialog open={open} onOpenChange={onOpenChange}>
			<AlertDialogContent>
				<AlertDialogHeader>
					<AlertDialogTitle>
						Permanently delete subscription account?
					</AlertDialogTitle>
					<AlertDialogDescription>
						This removes the Clerical account row, usage aggregate, model
						catalog, quota state, and thread bindings. Historical billing events
						remain for audit. This action cannot be undone.
					</AlertDialogDescription>
				</AlertDialogHeader>
				<AlertDialogFooter>
					<AlertDialogCancel disabled={pending}>Cancel</AlertDialogCancel>
					<AlertDialogAction
						variant="destructive"
						disabled={pending}
						onClick={onConfirm}
					>
						{pending ? (
							<Spinner data-icon="inline-start" />
						) : (
							<Trash2Icon data-icon="inline-start" />
						)}
						Delete permanently
					</AlertDialogAction>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	);
}

export function AccountDetailSheet({
	account,
	onClose,
	onReconnect,
}: {
	account: AccountRow | null;
	onClose: () => void;
	onReconnect: (account: AccountRow) => void;
}) {
	const detail = useQuery(
		api.llmSubscriptions.accounts.getAccountForStaff,
		account ? { accountId: account.accountId } : "skip",
	);
	const setStatus = useMutation(api.llmSubscriptions.accounts.setAccountStatus);
	const deleteAccount = useMutation(
		api.llmSubscriptions.accounts.deleteRevokedAccount,
	);
	const testCodexAccount = useAction(
		api.llmSubscriptions.codexActions.testAccount,
	);
	const testAnthropicAccount = useAction(
		api.llmSubscriptions.anthropicActions.testAccount,
	);
	const revokeCodexAccount = useAction(
		api.llmSubscriptions.codexActions.revokeAccount,
	);
	const disconnectAnthropicAccount = useAction(
		api.llmSubscriptions.anthropicActions.disconnectAccount,
	);
	const refreshAnthropicUsage = useAction(
		api.llmSubscriptions.anthropicActions.refreshUsageForStaff,
	);
	const [pendingAction, setPendingAction] = useState<string | null>(null);
	const [revokeOpen, setRevokeOpen] = useState(false);
	const [deleteOpen, setDeleteOpen] = useState(false);

	const cacheHitRate = useMemo(() => {
		if (!detail || detail.directCacheUsage.inputTokens === 0) return 0;
		return (
			(detail.directCacheUsage.cachedInputTokens /
				detail.directCacheUsage.inputTokens) *
			100
		);
	}, [detail]);

	async function changeStatus(status: "active" | "paused") {
		if (!account) return;
		setPendingAction(status);
		try {
			await setStatus({ accountId: account.accountId, status });
			toast.success(status === "active" ? "Account resumed" : "Account paused");
		} catch (error) {
			toast.error(errorCopy(error));
		} finally {
			setPendingAction(null);
		}
	}

	async function runTest() {
		if (!account) return;
		setPendingAction("test");
		try {
			const result =
				account.provider === "anthropic"
					? await testAnthropicAccount({ accountId: account.accountId })
					: await testCodexAccount({ accountId: account.accountId });
			toast.success(
				`Verified ${result.model}: ${result.cacheMode} cache, ${number.format(result.cachedInputTokens)} cached tokens`,
			);
		} catch (error) {
			toast.error(errorCopy(error));
		} finally {
			setPendingAction(null);
		}
	}

	async function refreshQuota() {
		if (account?.provider !== "anthropic") return;
		setPendingAction("quota");
		try {
			const result = await refreshAnthropicUsage({
				accountId: account.accountId,
			});
			toast.success(
				result.refreshed
					? "Anthropic quota refreshed"
					: "Anthropic quota refresh already running",
			);
		} catch (error) {
			toast.error(errorCopy(error));
		} finally {
			setPendingAction(null);
		}
	}

	async function revoke() {
		if (!account) return;
		setPendingAction("revoke");
		try {
			const changed =
				account.provider === "anthropic"
					? (
							await disconnectAnthropicAccount({
								accountId: account.accountId,
							})
						).disconnected
					: (
							await revokeCodexAccount({
								accountId: account.accountId,
							})
						).revoked;
			if (changed) {
				toast.success(
					account.provider === "anthropic"
						? "Anthropic credentials disconnected and removed"
						: "Provider credentials revoked and removed",
				);
			} else {
				toast("Account credentials were already removed.");
			}
			setRevokeOpen(false);
			onClose();
		} catch (error) {
			toast.error(errorCopy(error));
		} finally {
			setPendingAction(null);
		}
	}

	async function deleteRecord() {
		if (account?.status !== "revoked") return;
		setPendingAction("delete");
		try {
			const result = await deleteAccount({ accountId: account.accountId });
			if (result.deleted) {
				toast.success("Subscription account deleted");
			} else {
				toast("Subscription account was already deleted.");
			}
			setDeleteOpen(false);
			onClose();
		} catch (error) {
			toast.error(errorCopy(error));
		} finally {
			setPendingAction(null);
		}
	}

	return (
		<>
			<Sheet
				open={account !== null}
				onOpenChange={(open) => !open && onClose()}
			>
				<SheetContent className="w-full sm:min-w-[40vw] sm:max-w-[40vw]">
					<SheetHeader className="border-b">
						<div className="flex items-center gap-2">
							<SheetTitle>
								{account?.label ?? "Subscription account"}
							</SheetTitle>
							{account ? <StatusBadge status={account.status} /> : null}
						</div>
						<SheetDescription>
							{account?.providerEmail} · {account?.providerAccountIdMasked}
							{account?.providerPlan ? ` · ${account.providerPlan}` : ""}
						</SheetDescription>
					</SheetHeader>
					<ScrollArea className="min-h-0 flex-1">
						{detail === undefined ? (
							<div className="flex flex-col gap-3 px-4">
								<Skeleton className="h-32 w-full" />
								<Skeleton className="h-48 w-full" />
							</div>
						) : detail === null ? (
							<Empty className="min-h-64 border-0">
								<EmptyHeader>
									<EmptyMedia variant="icon">
										<KeyRoundIcon />
									</EmptyMedia>
									<EmptyTitle>Account unavailable</EmptyTitle>
									<EmptyDescription>
										Account was removed or is no longer accessible. Refresh
										account list.
									</EmptyDescription>
								</EmptyHeader>
							</Empty>
						) : (
							<div className="flex flex-col gap-4 px-4 pb-6">
								<div className="flex flex-wrap gap-2">
									<Button disabled={pendingAction !== null} onClick={runTest}>
										{pendingAction === "test" ? (
											<Spinner data-icon="inline-start" />
										) : (
											<ActivityIcon data-icon="inline-start" />
										)}
										Test account
									</Button>
									{account?.provider === "anthropic" ? (
										<Button
											variant="outline"
											disabled={pendingAction !== null}
											onClick={refreshQuota}
										>
											{pendingAction === "quota" ? (
												<Spinner data-icon="inline-start" />
											) : (
												<RefreshCwIcon data-icon="inline-start" />
											)}
											Refresh quota
										</Button>
									) : null}
									{account?.status === "active" ? (
										<Button
											variant="outline"
											disabled={pendingAction !== null}
											onClick={() => changeStatus("paused")}
										>
											<PauseIcon data-icon="inline-start" />
											Pause
										</Button>
									) : account?.status === "paused" ? (
										<Button
											variant="outline"
											disabled={pendingAction !== null}
											onClick={() => changeStatus("active")}
										>
											<PlayIcon data-icon="inline-start" />
											Resume
										</Button>
									) : null}
									{account &&
									account.status !== "revoked" &&
									account.status !== "revoking" ? (
										<Button
											variant="outline"
											disabled={pendingAction !== null}
											onClick={() => onReconnect(account)}
										>
											<RefreshCwIcon data-icon="inline-start" />
											Reconnect
										</Button>
									) : null}
									{account && account.status !== "revoked" ? (
										<Button
											variant="destructive"
											disabled={pendingAction !== null}
											onClick={() => setRevokeOpen(true)}
										>
											<Trash2Icon data-icon="inline-start" />
											{account.status === "revoking"
												? account.provider === "anthropic"
													? "Retry disconnect"
													: "Retry revoke"
												: account.provider === "anthropic"
													? "Disconnect"
													: "Revoke"}
										</Button>
									) : null}
									{account?.status === "revoked" ? (
										<Button
											variant="destructive"
											disabled={pendingAction !== null}
											onClick={() => setDeleteOpen(true)}
										>
											<Trash2Icon data-icon="inline-start" />
											Delete record
										</Button>
									) : null}
								</div>
								<div className="grid gap-4 sm:grid-cols-3">
									<Card>
										<CardHeader>
											<CardDescription>Generations</CardDescription>
											<CardTitle className="tabular-nums">
												{number.format(detail.usage.generations)}
											</CardTitle>
										</CardHeader>
										<CardContent className="text-xs text-muted-foreground">
											{number.format(detail.usage.inputTokens)} input ·{" "}
											{number.format(detail.usage.outputTokens)} output
										</CardContent>
									</Card>
									<Card>
										<CardHeader>
											<CardDescription>
												Recent direct cache hit rate
											</CardDescription>
											<CardTitle className="tabular-nums">
												{cacheHitRate.toFixed(1)}%
											</CardTitle>
										</CardHeader>
										<CardContent className="flex flex-col gap-2">
											<Progress value={cacheHitRate} />
											<p className="text-xs text-muted-foreground">
												{number.format(
													detail.directCacheUsage.cachedInputTokens,
												)}{" "}
												cached of{" "}
												{number.format(detail.directCacheUsage.inputTokens)}{" "}
												input ·{" "}
												{number.format(detail.directCacheUsage.generations)}{" "}
												generations
											</p>
										</CardContent>
									</Card>
									<Card>
										<CardHeader>
											<CardDescription>Billable cost</CardDescription>
											<CardTitle className="tabular-nums">
												{currency.format(detail.usage.costUsd)}
											</CardTitle>
										</CardHeader>
										<CardContent className="text-xs text-muted-foreground">
											OpenRouter-reference tariff total
										</CardContent>
									</Card>
								</div>
								<QuotaWindowsCard account={detail.summary} />
								<Card>
									<CardHeader>
										<CardTitle>Live models</CardTitle>
										<CardDescription>
											Catalog routes proved for this exact account.
										</CardDescription>
									</CardHeader>
									<CardContent className="p-0">
										<Table>
											<TableHeader>
												<TableRow>
													<TableHead>Model</TableHead>
													<TableHead className="text-right">Context</TableHead>
													<TableHead className="text-right">Fetched</TableHead>
												</TableRow>
											</TableHeader>
											<TableBody>
												{detail.models.map((model) => (
													<TableRow key={model.subscriptionModelId}>
														<TableCell>
															<div className="flex flex-col gap-0.5">
																<span className="font-medium">
																	{model.subscriptionModelId}
																</span>
																<span className="text-xs text-muted-foreground">
																	{model.displayName}
																</span>
															</div>
														</TableCell>
														<TableCell className="text-right tabular-nums">
															{number.format(model.contextWindowTokens)}
														</TableCell>
														<TableCell className="text-right text-muted-foreground">
															{formatDate(model.fetchedAt)}
														</TableCell>
													</TableRow>
												))}
											</TableBody>
										</Table>
									</CardContent>
								</Card>
								<TariffsCard tariffs={detail.tariffs} />
							</div>
						)}
					</ScrollArea>
				</SheetContent>
			</Sheet>
			<AlertDialog open={revokeOpen} onOpenChange={setRevokeOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{account?.provider === "anthropic"
								? account.status === "revoking"
									? "Retry Anthropic disconnect?"
									: "Disconnect Anthropic credentials?"
								: account?.status === "revoking"
									? "Retry provider credential revocation?"
									: "Revoke provider credentials?"}
						</AlertDialogTitle>
						<AlertDialogDescription>
							{account?.provider === "anthropic"
								? "Encrypted OAuth credentials will be deleted locally. Anthropic exposes no approved token-revocation endpoint."
								: "Provider tokens will be revoked and encrypted credentials deleted."}{" "}
							Existing thread bindings remain tombstones and use fallback.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={pendingAction === "revoke"}>
							Cancel
						</AlertDialogCancel>
						<AlertDialogAction
							disabled={pendingAction === "revoke"}
							onClick={revoke}
						>
							{pendingAction === "revoke" ? (
								<Spinner data-icon="inline-start" />
							) : null}
							{account?.provider === "anthropic"
								? account.status === "revoking"
									? "Retry disconnect"
									: "Disconnect"
								: account?.status === "revoking"
									? "Retry revoke"
									: "Revoke"}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
			<DeleteAccountDialog
				open={deleteOpen}
				pending={pendingAction === "delete"}
				onOpenChange={setDeleteOpen}
				onConfirm={deleteRecord}
			/>
		</>
	);
}

export default function LlmSubscriptionAccountsPage() {
	const accounts: AccountRow[] | undefined = useQuery(
		api.llmSubscriptions.accounts.listAccountsForStaff,
		{},
	);
	const codexSettings = useQuery(
		api.llmSubscriptions.accounts.getProviderSettingsForStaff,
		{ provider: "codex" },
	);
	const anthropicSettings = useQuery(
		api.llmSubscriptions.accounts.getProviderSettingsForStaff,
		{ provider: "anthropic" },
	);
	const setRoutingEnabled = useMutation(
		api.llmSubscriptions.accounts.setProviderRoutingEnabled,
	);
	const [selectedAccountId, setSelectedAccountId] =
		useState<Id<"llmSubscriptionAccounts"> | null>(null);
	const [connectOpen, setConnectOpen] = useState(false);
	// Bumped per open; remounts dialog with fresh state (no reset effect).
	const [connectInstance, setConnectInstance] = useState(0);
	const [reconnectAccount, setReconnectAccount] = useState<AccountRow | null>(
		null,
	);
	const [routingPending, setRoutingPending] = useState<
		"codex" | "anthropic" | null
	>(null);
	const selectedAccount =
		accounts?.find((account) => account.accountId === selectedAccountId) ??
		null;

	async function changeRouting(
		provider: "codex" | "anthropic",
		enabled: boolean,
	) {
		setRoutingPending(provider);
		const providerName = provider === "anthropic" ? "Anthropic" : "Codex";
		try {
			await setRoutingEnabled({ provider, enabled });
			toast.success(
				enabled
					? `${providerName} direct routing enabled`
					: `${providerName} direct routing disabled`,
			);
		} catch (error) {
			toast.error(errorCopy(error));
		} finally {
			setRoutingPending(null);
		}
	}

	function openConnect() {
		setReconnectAccount(null);
		setConnectInstance((instance) => instance + 1);
		setConnectOpen(true);
	}

	function openReconnect(account: AccountRow) {
		setSelectedAccountId(null);
		setReconnectAccount(account);
		setConnectInstance((instance) => instance + 1);
		setConnectOpen(true);
	}

	return (
		<div className="flex h-full min-h-0 flex-col bg-background">
			<header className="flex flex-col gap-3 border-b px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:pl-14 sm:pr-6">
				<div className="flex flex-col gap-1">
					<h1 className="text-lg font-semibold">LLM subscriptions</h1>
					<p className="text-xs text-muted-foreground">
						Global encrypted provider accounts, cache affinity, and
						direct-routing health.
					</p>
				</div>
				<div className="flex flex-wrap items-center gap-3">
					{(
						[
							["codex", "Codex", codexSettings],
							["anthropic", "Anthropic", anthropicSettings],
						] as const
					).map(([provider, label, settings]) => (
						<div
							key={provider}
							className="flex items-center gap-2 rounded-lg border px-3 py-2"
						>
							<CloudCogIcon className="size-4 text-muted-foreground" />
							<label
								htmlFor={`${provider}-routing`}
								className="text-sm font-medium"
							>
								{label} routing
							</label>
							<Switch
								id={`${provider}-routing`}
								checked={settings?.routingEnabled ?? false}
								disabled={settings === undefined || routingPending !== null}
								onCheckedChange={(enabled) => changeRouting(provider, enabled)}
							/>
						</div>
					))}
					<Button onClick={openConnect}>
						<PlusIcon data-icon="inline-start" />
						Connect account
					</Button>
				</div>
			</header>
			<main className="flex min-h-0 flex-1 flex-col">
				<ScrollArea className="min-h-0 flex-1">
					{accounts === undefined ? (
						<div className="flex flex-col gap-3 p-4">
							<Skeleton className="h-16 w-full" />
							<Skeleton className="h-16 w-full" />
						</div>
					) : accounts.length === 0 ? (
						<Empty className="min-h-96 border-0">
							<EmptyHeader>
								<EmptyMedia variant="icon">
									<KeyRoundIcon />
								</EmptyMedia>
								<EmptyTitle>No subscription accounts</EmptyTitle>
								<EmptyDescription>
									Connect Codex or Anthropic, prove prompt caching, then enable
									direct routing.
								</EmptyDescription>
							</EmptyHeader>
							<EmptyContent>
								<Button onClick={openConnect}>Connect account</Button>
							</EmptyContent>
						</Empty>
					) : (
						<AccountsTable
							accounts={accounts}
							onSelect={setSelectedAccountId}
						/>
					)}
				</ScrollArea>
			</main>
			<ConnectAccountDialog
				key={connectInstance}
				open={connectOpen}
				onOpenChange={setConnectOpen}
				reconnectAccount={reconnectAccount}
			/>
			<AccountDetailSheet
				account={selectedAccount}
				onClose={() => setSelectedAccountId(null)}
				onReconnect={openReconnect}
			/>
		</div>
	);
}
